/**
 * useAndiRealtime — browser-side xAI Realtime voice client for Hey Andi.
 *
 * Security: never sees the master xAI key. It asks our backend
 * (POST /api/voice/realtime/client-secret) for a short-lived ephemeral secret,
 * then opens wss://api.x.ai/v1/realtime directly with that secret as a
 * sec-websocket-protocol entry (prefix `xai-client-secret.`).
 *
 * Audio: 24 kHz mono PCM16 in and out. Mic → input_audio_buffer.append;
 * server VAD drives turns and barge-in; Andi audio arrives as
 * response.output_audio.delta and is queued to the speaker.
 */
import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const RATE = 24000;

export const RT_STATE = { IDLE: 'idle', CONNECTING: 'connecting', LISTENING: 'listening', THINKING: 'thinking', SPEAKING: 'speaking', ERROR: 'error' };

function b64ToInt16(b64) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Int16Array(bytes.buffer);
}
function floatToB64PCM16(float32) {
  const buf = new ArrayBuffer(float32.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < float32.length; i++) {
    let s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function useAndiRealtime({ headers, instructions, voice, contextClient, extraContext, onUserTranscript, onAndiTranscript, onError } = {}) {
  const [state, setState] = useState(RT_STATE.IDLE);
  const [muted, setMuted] = useState(false);
  const [level, setLevel] = useState(0);
  const wsRef = useRef(null);
  const micCtxRef = useRef(null);
  const playCtxRef = useRef(null);
  const streamRef = useRef(null);
  const procRef = useRef(null);
  const playHeadRef = useRef(0);
  const mutedRef = useRef(false);
  const stoppedRef = useRef(false);

  const stopPlayback = useCallback(() => {
    const ctx = playCtxRef.current;
    if (ctx) playHeadRef.current = ctx.currentTime;
  }, []);

  const enqueueAudio = useCallback((int16) => {
    const ctx = playCtxRef.current;
    if (!ctx) return;
    const f32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) f32[i] = int16[i] / 0x8000;
    const buffer = ctx.createBuffer(1, f32.length, RATE);
    buffer.copyToChannel(f32, 0);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    const start = Math.max(now, playHeadRef.current);
    src.start(start);
    playHeadRef.current = start + buffer.duration;
    setState((s) => (s === RT_STATE.ERROR ? s : RT_STATE.SPEAKING));
  }, []);

  const cleanup = useCallback(() => {
    stoppedRef.current = true;
    try { procRef.current && procRef.current.disconnect(); } catch { /* noop */ }
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { micCtxRef.current && micCtxRef.current.close(); } catch { /* noop */ }
    try { playCtxRef.current && playCtxRef.current.close(); } catch { /* noop */ }
    try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    procRef.current = null; streamRef.current = null; micCtxRef.current = null; playCtxRef.current = null; wsRef.current = null;
  }, []);

  const disconnect = useCallback(() => { cleanup(); setState(RT_STATE.IDLE); setLevel(0); }, [cleanup]);

  const connect = useCallback(async () => {
    stoppedRef.current = false;
    setState(RT_STATE.CONNECTING);
    let secret, wsUrl, sessVoice, grounded = instructions;
    try {
      const { data } = await axios.post(`${API}/api/voice/realtime/client-secret`, { voice, seconds: 600 }, { headers });
      secret = data.value; wsUrl = data.ws_url; sessVoice = data.voice;
    } catch (e) {
      setState(RT_STATE.ERROR); onError?.('Could not start voice (auth).'); return false;
    }
    try {
      const { data: c } = await axios.get(`${API}/api/voice/session-context`, { params: contextClient ? { client: contextClient } : {}, headers });
      if (c?.instructions) grounded = c.instructions;
      if (c?.voice) sessVoice = sessVoice || c.voice;
    } catch { /* grounding is best-effort */ }
    if (extraContext) grounded = `${grounded || instructions || ''}\n\n${extraContext}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl, ['realtime', `xai-client-secret.${secret}`]);
    } catch {
      setState(RT_STATE.ERROR); onError?.('Voice connection failed.'); return false;
    }
    wsRef.current = ws;

    ws.onopen = async () => {
      ws.send(JSON.stringify({
        type: 'session.update',
        session: {
          instructions: grounded || instructions || 'You are Hey Andi, a concise, calm, confident voice assistant. CRITICAL: do not speak or greet when the session starts — stay silent until the user actually asks something, then answer briefly and wait silently. Keep spoken replies short and conversational.',
          voice: sessVoice || voice || 'eve',
          modalities: ['audio', 'text'],
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
          input_audio_transcription: { model: 'grok-transcribe' },
          turn_detection: { type: 'server_vad', threshold: 0.5, silence_duration_ms: 500 },
        },
      }));
      try {
        const playCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: RATE });
        playCtxRef.current = playCtx; playHeadRef.current = playCtx.currentTime;
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
        streamRef.current = stream;
        const micCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: RATE });
        micCtxRef.current = micCtx;
        const source = micCtx.createMediaStreamSource(stream);
        const proc = micCtx.createScriptProcessor(4096, 1, 1);
        procRef.current = proc;
        proc.onaudioprocess = (ev) => {
          if (stoppedRef.current || mutedRef.current || ws.readyState !== 1) return;
          const input = ev.inputBuffer.getChannelData(0);
          let peak = 0;
          for (let i = 0; i < input.length; i++) peak = Math.max(peak, Math.abs(input[i]));
          setLevel(peak);
          ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: floatToB64PCM16(input) }));
        };
        source.connect(proc);
        proc.connect(micCtx.destination);
        setState(RT_STATE.LISTENING);
      } catch {
        setState(RT_STATE.ERROR); onError?.('Microphone access was blocked.'); cleanup();
      }
    };

    ws.onmessage = (ev) => {
      let msg; try { msg = JSON.parse(ev.data); } catch { return; }
      switch (msg.type) {
        case 'input_audio_buffer.speech_started':
          stopPlayback();  // barge-in: user interrupted Andi
          setState(RT_STATE.LISTENING);
          break;
        case 'input_audio_buffer.speech_stopped':
          setState(RT_STATE.THINKING);
          break;
        case 'conversation.item.input_audio_transcription.updated':
          onUserTranscript?.(msg.transcript || msg.delta || '', false);
          break;
        case 'conversation.item.input_audio_transcription.completed':
          onUserTranscript?.(msg.transcript || '', true);
          break;
        case 'response.output_audio_transcript.delta':
          onAndiTranscript?.(msg.delta || '', false);
          break;
        case 'response.output_audio_transcript.done':
          onAndiTranscript?.(msg.transcript || '', true);
          break;
        case 'response.output_audio.delta':
          if (msg.delta) enqueueAudio(b64ToInt16(msg.delta));
          break;
        case 'response.done':
          setState((s) => (s === RT_STATE.SPEAKING ? RT_STATE.LISTENING : s));
          break;
        case 'error':
          // Most errors are recoverable; surface only fatal-looking ones.
          if (msg.error && /auth|closed|fatal/i.test(JSON.stringify(msg.error))) { onError?.('Voice error.'); }
          break;
        default: break;
      }
    };
    ws.onerror = () => { if (!stoppedRef.current) { setState(RT_STATE.ERROR); onError?.('Voice connection interrupted.'); } };
    ws.onclose = () => { if (!stoppedRef.current) { setState(RT_STATE.IDLE); } };
    return true;
  }, [headers, instructions, voice, contextClient, extraContext, onError, onUserTranscript, onAndiTranscript, enqueueAudio, stopPlayback, cleanup]);

  const toggleMute = useCallback(() => { mutedRef.current = !mutedRef.current; setMuted(mutedRef.current); }, []);

  return { state, level, muted, connect, disconnect, toggleMute,
    isLive: state !== RT_STATE.IDLE && state !== RT_STATE.ERROR };
}
